import { useState } from "react";
import { Mic, MicOff, ShieldQuestion, Loader2, Send, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const UI = {
  other_voice: { label: "Voice consistency: possible other voice", cls: "border-rose-500/30 bg-rose-500/10 text-rose-300", Icon: MicOff },
  enrolled: { label: "Voice consistency: enrolled ✓", cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", Icon: Mic },
  not_enrolled: { label: "Voice consistency: not enrolled", cls: "border-border bg-muted/40 text-muted-foreground", Icon: ShieldQuestion },
};

// Small advisory line on the candidate integrity view summarising the voice-consistency check.
// Recruiters can ask the candidate to re-record their reference voice when a different voice is suspected.
export function VoiceSignal({ signal, candId, recruiter = false }) {
  const [requestedAt, setRequestedAt] = useState(signal?.reenroll_requested_at || null);
  const [busy, setBusy] = useState(false);
  if (!signal) return null;
  const u = UI[signal.status] || UI.not_enrolled;
  const { Icon } = u;
  const canAsk = recruiter && candId && signal.status === "other_voice";

  const ask = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${candId}/voice/reenroll-request`);
      setRequestedAt(data.reenroll_requested_at || new Date().toISOString());
      toast.success("Asked the candidate to re-record their voice sample");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the request");
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border p-3 flex items-start gap-2 ${u.cls}`} data-testid="ci-voice-signal" data-voice-status={signal.status}>
      <Icon className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{u.label}</div>
        {signal.reason && <p className="text-xs text-muted-foreground leading-relaxed">{signal.reason}</p>}
        {requestedAt ? (
          <p className="text-[11px] text-muted-foreground mt-1.5 inline-flex items-center gap-1" data-testid="voice-reenroll-requested">
            <CheckCircle2 className="h-3 w-3" /> Re-record requested · {new Date(requestedAt).toLocaleString()}
          </p>
        ) : canAsk ? (
          <button onClick={ask} disabled={busy} className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-rose-200 hover:text-rose-100 disabled:opacity-60" data-testid="voice-reenroll-btn">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Ask candidate to re-record voice
          </button>
        ) : null}
      </div>
    </div>
  );
}
